import React, { useEffect, useState } from 'react';
import { useContextMenu } from 'react-contexify';
import { WrappedComponentProps as InjectedIntlProps } from 'react-intl';

import { TAB_MAX_WIDTH } from '../../../shared/constants';
import { formatMessageHelper } from '../../../shared/helpers/internationalization';
import { isMobile } from '../../../shared/helpers/responsive';

import './EditorTab.scss';
import EditorTabInsideElement from './EditorTabInsideElement';

const MENU_TYPE_1 = 'MULTI_1';
const MENU_TYPE_2 = 'MULTI_2';

const extractTabNumber = (tabName: string) => {
  const tabDefaultNames = ['Super Simulation', 'Multi-Simulation', 'Simulation'];
  let defaultTabName;

  for (defaultTabName of tabDefaultNames) {
    const simultationParser: any = tabName.split(defaultTabName);
    if (simultationParser.length === 2) {
      return simultationParser[1];
    }
  }

  return null;
};

interface EditorTabProps {
  tabName: string;
  tabWidth?: number;
  active?: boolean;
  multi?: boolean;
  isSuperAlpha?: boolean;
  onActivateClickHandler?: () => void;
  onCloseClickHandler?: (event: React.SyntheticEvent<HTMLSpanElement>) => void;
  progress: number;
  handleRenameTab?: (e: any) => void;
  handleDisableRename?: (e: any) => void;
  canRename: boolean;
  currentIndex: number;
  isChild: boolean;
  tabStatus: string | undefined;
  hasAlpha: boolean;
  updateTabWidth?: () => void;
}

const EditorTab = ({
  tabName,
  tabWidth = TAB_MAX_WIDTH,
  active = false,
  onActivateClickHandler,
  onCloseClickHandler,
  multi,
  isSuperAlpha,
  progress,
  handleRenameTab,
  handleDisableRename,
  canRename,
  currentIndex,
  isChild,
  tabStatus,
  hasAlpha,
  updateTabWidth,
  intl,
}: EditorTabProps & InjectedIntlProps): JSX.Element => {
  const [tabNameOverflowed, setTabNameOverflowed] = useState<boolean>(false);
  const [tabNumber, setTabNumber] = useState<number | null>(null);
  useEffect(() => {
    setTabNumber(extractTabNumber(tabName));
    if (active && updateTabWidth) {
      updateTabWidth();
    }
    if (tabNumber && intl && tabName && tabName.includes(formatMessageHelper(intl, 'editor.multi-simulation'))) {
      if (tabNumber >= 10) {
        setTabNameOverflowed(true);
      } else {
        setTabNameOverflowed(!active && tabWidth <= 193);
      }
    } else if (tabNumber && intl && tabName && tabName.includes(formatMessageHelper(intl, 'editor.super-simulation'))) {
      if (tabNumber >= 10) {
        setTabNameOverflowed(true);
      } else {
        setTabNameOverflowed(!active && tabWidth <= 198);
      }
    } else if (!active && tabNumber && intl && tabName && tabName.includes(formatMessageHelper(intl, 'editor.simulation'))) {
      if (!isChild) {
        setTabNameOverflowed(tabWidth <= 165);
      } else {
        setTabNameOverflowed(tabWidth <= 126);
      }
    } else {
      setTabNameOverflowed(false);
    }
  }, [active, currentIndex, tabName, tabWidth, tabNumber]);
  const tabElementClass = `editor-tabs__tab-element ${
    multi ? 'editor-tabs__multi-tab-element' : `editor-tabs__${isSuperAlpha ? 'super-alpha-' : isChild ? 'child-' : ''}tab-element`
  }${active ? '--active' : ''}`;

  const disableRename = (e: any) => {
    if (handleDisableRename && e.key === 'Enter') {
      setTabNumber(null);
      handleDisableRename(e);
    }
  };

  const handleClickTabDiv = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onActivateClickHandler?.();
  };

  const handleClickTabContent = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onActivateClickHandler?.();
  };

  const { show: showContextMenu } = useContextMenu();
  const onContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    showContextMenu({
      event,
      id: hasAlpha ? MENU_TYPE_1 : MENU_TYPE_2,
      props: {
        index: currentIndex,
        isChild: isChild,
        name: tabName,
        tabContainerElement: event.currentTarget,
      },
    });
  };

  const currentTabWidth = tabWidth >= TAB_MAX_WIDTH || active ? TAB_MAX_WIDTH : tabWidth;

  return (
    <div
      className={`${tabElementClass}${isMobile('mobile') ? '--mobile' : ''}`}
      onClick={handleClickTabDiv}
      style={{ width: isMobile('mobile') ? '100%' : `${currentTabWidth}px` }}
    >
      <div className="contexify-wrapper" onContextMenu={onContextMenu}>
        <EditorTabInsideElement
          tabName={tabName}
          tabNumber={tabNumber}
          tabWidth={tabWidth}
          active={active}
          multi={multi}
          isSuperAlpha={isSuperAlpha}
          progress={progress}
          tabElementClass={tabElementClass}
          currentTabWidth={currentTabWidth}
          tabNameOverflowed={tabNameOverflowed}
          onActivateClickHandler={handleClickTabContent}
          onCloseClickHandler={onCloseClickHandler}
          handleRenameTab={handleRenameTab}
          handleDisableRename={handleDisableRename}
          disableRename={disableRename}
          canRename={canRename}
          isChild={isChild}
          tabStatus={tabStatus}
        />
      </div>
    </div>
  );
};

export default EditorTab;
